import { MediaUploadModal } from "@/components/media/media-upload-modal";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MEETING_SUBTYPES, MEETING_TYPES } from "@/lib/meetings-constants";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FileText, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  datetime: z.string().min(1, "Date and time is required"),
  meetingType: z.enum([
    "city-council",
    "planning-zoning",
    "board-adjustment",
    "historic-preservation-committee",
  ]),
  meetingSubtype: z
    .enum(["regular", "special-meeting", "public-hearing", "public-notice"])
    .optional(),
  location: z.string().optional(),
  description: z.string().optional(),
  agendaMediaId: z.string().optional(),
  minutesMediaId: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

function NewMeetingRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const [selectedAgenda, setSelectedAgenda] = useState<string | undefined>();
  const [selectedMinutes, setSelectedMinutes] = useState<string | undefined>();
  const [agendaModalOpen, setAgendaModalOpen] = useState(false);
  const [minutesModalOpen, setMinutesModalOpen] = useState(false);

  // Get available media for agenda/minutes selection
  const mediaQuery = useQuery(trpc.media.list.queryOptions());
  const media = mediaQuery.data || [];

  // Clear stale media selections when media list updates
  useEffect(() => {
    const mediaIds = new Set(media.map((m) => m.id));

    if (selectedAgenda && !mediaIds.has(selectedAgenda)) {
      setSelectedAgenda(undefined);
      toast.warning(
        "The selected agenda document is no longer available and has been cleared.",
      );
    }

    if (selectedMinutes && !mediaIds.has(selectedMinutes)) {
      setSelectedMinutes(undefined);
      toast.warning(
        "The selected minutes document is no longer available and has been cleared.",
      );
    }
  }, [media, selectedAgenda, selectedMinutes]);

  const {
    control,
    register,
    handleSubmit,
    setValue: _setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      datetime: new Date().toISOString(),
      meetingType: "city-council",
    },
  });

  const createMutation = useMutation(
    trpc.meetings.create.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["meetings"] });
        queryClient.invalidateQueries({ queryKey: ["media"] });
        navigate({ to: "/meetings" });
      },
      onError: (error) => {
        // Show user-friendly error message
        toast.error(error.message || "Failed to create meeting");
      },
    }),
  );

  const onSubmit = async (data: FormData) => {
    await createMutation.mutateAsync({
      ...data,
      date: new Date(data.datetime),
      agendaMediaId: selectedAgenda,
      minutesMediaId: selectedMinutes,
    });
  };

  return (
    <div className="mx-auto grid max-w-3xl gap-4">
      <div className="flex items-center gap-4">
        <h1 className="text-3xl font-semibold">Create New Meeting</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Meeting Details</CardTitle>
            <CardDescription>
              Enter the basic information about the meeting.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g., Regular City Council Meeting"
                {...register("title")}
              />
              {errors.title && (
                <p className="text-sm text-red-500">{errors.title.message}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Controller
                  name="datetime"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label="Date & Time"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select date and time"
                    />
                  )}
                />
                {errors.datetime && (
                  <p className="text-sm text-red-500">
                    {errors.datetime.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="meetingType">Meeting Type</Label>
                <Controller
                  name="meetingType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {MEETING_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="meetingSubtype">Meeting Subtype</Label>
                <Controller
                  name="meetingSubtype"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select subtype (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {MEETING_SUBTYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  placeholder="e.g., City Hall, Room 201"
                  {...register("location")}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description or notes about the meeting"
                rows={3}
                {...register("description")}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Documents</CardTitle>
            <CardDescription>
              Select or upload agenda and minutes documents for this meeting.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Agenda Document</Label>
              <div className="flex gap-2">
                <Select
                  value={selectedAgenda}
                  onValueChange={setSelectedAgenda}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select existing document" />
                  </SelectTrigger>
                  <SelectContent>
                    {media.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAgendaModalOpen(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload New
                </Button>
              </div>
              {selectedAgenda && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Agenda selected
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Minutes Document</Label>
              <div className="flex gap-2">
                <Select
                  value={selectedMinutes}
                  onValueChange={setSelectedMinutes}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select existing document" />
                  </SelectTrigger>
                  <SelectContent>
                    {media.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMinutesModalOpen(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload New
                </Button>
              </div>
              {selectedMinutes && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Minutes selected
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={isSubmitting || createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Meeting"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ to: "/meetings" })}
          >
            Cancel
          </Button>
        </div>
      </form>

      <MediaUploadModal
        open={agendaModalOpen}
        onOpenChange={setAgendaModalOpen}
        onSuccess={(mediaId) => {
          setSelectedAgenda(mediaId);
          // Refetch media to get the updated list with proper tRPC query key
          queryClient.invalidateQueries(trpc.media.list.queryOptions());
        }}
      />

      <MediaUploadModal
        open={minutesModalOpen}
        onOpenChange={setMinutesModalOpen}
        onSuccess={(mediaId) => {
          setSelectedMinutes(mediaId);
          // Refetch media to get the updated list with proper tRPC query key
          queryClient.invalidateQueries(trpc.media.list.queryOptions());
        }}
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/meetings/new")({
  component: () => <NewMeetingRoute />,
});
